import {
  resolveSaasMediaUrl,
  type AudioGenerationParameterCapabilityField,
  type CanvasAudioGenerationDto,
  type GenerateCanvasAudioPayload,
} from '../../../api'
import { buildProviderOptionNodeDefaultFields } from './capabilityProviderSelection'

export const CANVAS_AUDIO_TEXT_MAX_LENGTH = 10000
export const AUDIO_GENERATION_CAPABILITY = 'audio.generation'
export const AUDIO_SKILL_KEY = 'audio_generation'
export const AUDIO_SKILL_VERSION = 'v1'

export type AudioGenerationHistoryEntry = {
  version: number
  audioAssetId: string | null
  artifactId: string | null
  textSnapshot: string | null
  voiceTemplateId: string | null
  systemVoiceKey: string | null
  voiceLabel: string | null
  providerOptionId: string | null
  skillKey: string | null
  skillVersion: string | null
  durationMs: number | null
  completedAt: string | null
  // VistoeraTASK-00217 — Generation History/Provenance must retain the
  // ACTUAL Pitch/Speed used for this past Generation (spec §22), so a
  // user can audit/restore exactly what produced a given past result.
  pitch?: number | null
  speed?: number | null
}

export function appendAudioGenerationHistoryEntry(
  history: AudioGenerationHistoryEntry[],
  entry: AudioGenerationHistoryEntry,
  maxEntries = 20,
): AudioGenerationHistoryEntry[] {
  return [...history, entry].slice(-maxEntries)
}

export function buildGenerateAudioNodeDefaultFields(): Record<string, unknown> {
  return {
    text: '',
    selectedVoiceTemplateId: null,
    selectedSystemVoiceKey: null,
    selectedVoiceLabel: null,
    connectedVoiceTemplateId: null,
    connectedVoiceLabel: null,
    status: 'READY',
    artifactId: null,
    audioAssetId: null,
    previousAudioAssetId: null,
    durationMs: null,
    errorMessage: null,
    errorCode: null,
    needsRegeneration: false,
    pendingVoiceTemplateCandidateNodeId: null,
    selectedVoiceTemplateExecutable: null,
    generationHistory: [],
    skillSelectionSource: 'AUTO',
    skillKey: null,
    skillVersion: null,
    // VistoeraTASK-00217 — `null` means "no explicit value saved on this
    // node yet"; the Slider then falls back to (1) the bound Voice
    // Template's own remembered preference, else (2) the active
    // Provider Capability's own default. Once the user drags a Slider,
    // this becomes a real persisted number via `patchFields` — never a
    // React-only/localStorage-only value (P0 persistence requirement).
    pitch: null,
    speed: null,
    ...buildProviderOptionNodeDefaultFields(),
  }
}

export function buildAudioGenerationPayload(args: {
  text: string
  voiceTemplateId: string | null
  systemVoiceKey: string | null
  pitch?: number | null
  speed?: number | null
}): GenerateCanvasAudioPayload {
  return {
    text: args.text.trim(),
    voice_template_id: args.voiceTemplateId,
    system_voice_key: args.systemVoiceKey,
    // Sent as-is (already clamped client-side to the capability-driven
    // Slider's own bounds) — the backend re-normalizes/clamps again
    // against the real active Provider capability regardless, so a
    // stale/out-of-range value can never reach the adapter even if this
    // client-side guard were ever bypassed.
    pitch: args.pitch ?? null,
    speed: args.speed ?? null,
  }
}

/** VistoeraTASK-00217 — frontend mirror of the backend's
 * `normalize_voice_generation_parameter()`, used ONLY for local Slider
 * clamping/default-fallback while dragging (never a substitute for the
 * backend's own authoritative re-normalization on every Generate call). */
export function normalizeVoiceGenerationParameterClientSide(
  value: number | null | undefined,
  field: AudioGenerationParameterCapabilityField | null | undefined,
): number | null {
  if (!field || !field.supported) return null
  let numeric = typeof value === 'number' && Number.isFinite(value) ? value : field.default
  if (numeric === null || numeric === undefined) return null
  if (field.min !== null && field.min !== undefined && numeric < field.min) numeric = field.min
  if (field.max !== null && field.max !== undefined && numeric > field.max) numeric = field.max
  return numeric
}

/**
 * MEDIA_ELEMENT_CROSS_HOST_URL_AUDIT_AND_FIX_V1 — this is handed straight
 * to a native `<audio src>` (via `VoiceAudioPlayer`) and a download
 * `<a href>` (via `downloadCanvasMedia`), neither of which goes through
 * `saasApi`'s axios `baseURL`. A bare `/api/saas/...` path here 200'd with
 * this frontend's own `index.html` (`text/html`) on Preview, where
 * `app-preview.vistoera.com` and `api-preview.vistoera.com` are different
 * origins — the exact same incident already fixed once for
 * `canvasVoiceTemplatePlaybackUrl` (Voice Template). `resolveSaasMediaUrl`
 * rewrites the backend-relative path to this API's real absolute origin.
 */
export function canvasAudioPlaybackUrl(workspaceId: string, assetId: string): string {
  return resolveSaasMediaUrl(`/api/saas/workspaces/${workspaceId}/assets/${assetId}/variants/original`) as string
}

export function mapAudioGenerationDtoToFields(dto: CanvasAudioGenerationDto): Record<string, unknown> {
  let status = 'READY'
  if (dto.status === 'GENERATING') status = 'GENERATING'
  else if (dto.status === 'SUCCEEDED') status = 'COMPLETED'
  else if (dto.status === 'FAILED') status = 'FAILED'

  return {
    artifactId: dto.artifact_id,
    audioAssetId: dto.audio_asset_id,
    durationMs: dto.duration_ms,
    status,
    errorMessage: dto.error_message,
    errorCode: dto.error_code,
    selectedVoiceTemplateId: dto.voice_template_id,
    selectedSystemVoiceKey: dto.system_voice_key,
    // VistoeraTASK-00217 — reflect back the ACTUAL normalized values the
    // Provider ran with (e.g. after backend clamping), so the Slider
    // never silently drifts from what really produced the audible
    // result on this Generation.
    ...(dto.pitch !== undefined ? { pitch: dto.pitch } : {}),
    ...(dto.speed !== undefined ? { speed: dto.speed } : {}),
  }
}
